
import { cardIsAllowed } from '@/checkout/helpers/allowedCards'
import FormInput2 from '@/components/forms/FormInput2.vue'
import { languageItem } from '@/helpers/LanguageHelpers'
import type { LanguageStrings } from '@/language/types'
import { ComponentOptions } from '@/types/ComponentOptions'
import { Stripe, StripeCardElement, StripeElements } from '@stripe/stripe-js'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'

/**
 * Single Stripe element that combines credit card number, expiry, CVC, and sometimes zip code.
 */
@Component({
  name: 'StripeCreditCard',
  components: { FormInput2 },
})
export default class extends Vue {
  @Prop({ required: true })
  stripe: Stripe

  card: StripeCardElement | null = null
  error: string | null = null

  t: LanguageStrings['stripeCreditCard']
  opt: ComponentOptions['stripeCreditCard']

  mounted() {
    this.createCardElement()
  }

  @Watch('stripe')
  createCardElement() {
    const elements: StripeElements = this.stripe.elements(this.stripeOptions)
    this.card = elements.create('card', {
      hidePostalCode: true,
      style: this.elementStyle,
    })

    this.card.mount(this.stripeElement)

    // Watch the rolling update of error state
    this.card.on('change', (card) => {
      if (cardIsAllowed(card)) {
        this.error = card.error?.message ?? null
      } else {
        this.error = languageItem('checkoutRoute').cardBrandError
      }
    })

    this.$emit('cardElement', this.card)

    this.card.on('focus', (e) => {
      this.$emit('focus', e)
    })
  }

  private get stripeElement() {
    return this.$refs.stripeElement as HTMLElement
  }

  private get stripeOptions() {
    const result: any = {}

    if (this.opt.font) {
      result.fonts = [this.opt.font]
    }
    if (this.opt.fontFaceStylesheet) {
      result.fonts = [
        {
          cssSrc: this.opt.fontFaceStylesheet,
        },
      ]
    }

    return result
  }

  private get elementStyle() {
    // Uses the <FormInput>'s <input> element as a style guide/source for the Stripe element style.
    const source = window.getComputedStyle(this.$el.querySelector('input') as HTMLInputElement)

    function val(name: string): string {
      return source.getPropertyValue(name)
    }

    // Get placeholder color
    // Parse input text color, converting to RGBA format and reduce opacity 50%
    const rgb = val('color')
      .replace(/[^\d,]/g, '')
      .split(',')
    const placeholder = `rgba(${rgb.join(',')}, 0.5)`

    return {
      base: {
        color: val('color'),
        fontFamily: val('font-family'),
        fontSize: val('font-size'),
        fontSmoothing: val('-webkit-font-smoothing'),
        fontStyle: val('font-style'),
        fontVariant: val('font-variant'),
        fontWeight: val('font-weight'),
        lineHeight: val('line-height'),
        '::placeholder': {
          color: placeholder,
        },
      },
    }
  }
}
